import { CircularProgress, IconButton, styled, Tooltip } from '@mui/material';
import { useCallback, useState } from 'react';

import { sendDatadogRumError } from 'utils/analytics/datadogRum';

/**
 * The borderless icon control used by list rows.
 *
 * One treatment with a density axis rather than a copy per row family: the two differ only in
 * how much room the row gives them, and duplicating the hover rule and the icon-colour selector
 * to express that produced two definitions that had to be kept in step by hand.
 *
 * `dense` is the tighter pairing, for rows that join into a single block.
 */
const RowIconButton = styled(IconButton, {
    shouldForwardProp: (prop) => prop !== 'dense',
})<{ dense?: boolean }>(({ theme, dense = false }) => ({
    width: dense ? 20 : 24,
    height: dense ? 20 : 24,
    padding: theme.spacing(0.25),
    '&:hover': { opacity: 0.7, backgroundColor: 'transparent' },
    // Sizes whatever icon it wraps, so callers don't restate it in `sx`.
    '& .MuiSvgIcon-root': { fontSize: dense ? 14 : 20, color: theme.palette.text.secondary },
}));

export default RowIconButton;

interface AsyncRowIconButtonProps {
    readonly icon: React.ReactNode;
    /** Accessible name. Required, because these controls have no visible text. */
    readonly label: string;
    readonly onClick: () => void | Promise<void>;
    readonly dense?: boolean;
    /** Held off from outside — while a sibling's action is in flight, say. Its own pending state disables it regardless. */
    readonly disabled?: boolean;
    /** Also show `label` on hover, for a control whose name carries more than its icon does. */
    readonly showLabelTooltip?: boolean;
}

/**
 * A row control whose action is in flight for a while — it disables itself and shows a spinner
 * until the returned promise settles.
 *
 * The guard is the point: without it a second click fires the same delete again, and a row
 * whose request is already away has nothing useful to do with the second one. Worth sharing
 * rather than re-deriving per row family, since every copy has to remember the `finally`.
 */
export function AsyncRowIconButton({
    icon,
    label,
    onClick,
    dense = false,
    disabled = false,
    showLabelTooltip = false,
}: Readonly<AsyncRowIconButtonProps>) {
    const [isPending, setIsPending] = useState(false);

    const handleClick = useCallback(async () => {
        try {
            const result = onClick();
            // Only an action still in flight is guarded. Disabling the control under one that
            // finished — that opened a dialog, say — would drop its focus, and the dialog would
            // then hand focus back to the page rather than to the control on close.
            if (!(result instanceof Promise)) {
                return;
            }
            setIsPending(true);
            await result;
        } catch (error) {
            // React discards the promise this handler returns, so a rejected action surfaced
            // only as an unhandled rejection: the spinner cleared and the row looked like it
            // had succeeded. Callers that can explain a failure still catch it themselves and
            // say so; this is the net for the ones that cannot, so the failure is at least
            // recorded rather than lost.
            sendDatadogRumError('Row action failed', error, { source: 'AsyncRowIconButton', label });
        } finally {
            setIsPending(false);
        }
    }, [onClick, label]);

    const button = (
        <RowIconButton
            onClick={handleClick}
            aria-label={label}
            disabled={isPending || disabled}
            dense={dense}
            disableRipple>
            {isPending ? <CircularProgress size={dense ? 14 : 16} /> : icon}
        </RowIconButton>
    );

    if (!showLabelTooltip) {
        return button;
    }
    // The span keeps hover working while the button is disabled, which swallows pointer events.
    // Inline-flex, so it is the button's size rather than a line box around it.
    // `describeChild`, so the span is not given the label too and the button stays its one owner.
    return (
        <Tooltip title={label} describeChild>
            <span style={{ display: 'inline-flex' }}>{button}</span>
        </Tooltip>
    );
}
